import { html, LitElement, PropertyValues, TemplateResult, unsafeCSS, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SearchEvents, DEFAULT_CODE_EDITOR_OPTIONS, THEMES } from '../types';
import { GzTextWebComponent } from '../../gzText';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { InputEvents } from '../../gzInput/types';
//tslint:disable-next-line:nx-enforce-module-boundaries
import { CodeEditor, EditorOptions, LuceneCodeEditorOptions, LuceneModelOptions } from '@nirvana/shared/code-editor';
//tslint:disable-next-line:nx-enforce-module-boundaries
import { styles as editorStyles } from 'libs/shared/code-editor/src/assets/editor.scss';
import { SearchQueryPanelEvents } from '../../../xdrSearchApp/xdrSearchQueryPanel/types';
import { FontSizes } from '../../../types/common/types';
import { GzButtonWebComponent } from '../../gzButton';
import { debounce } from '../../../utils/event.utils';
import { CodeEditorValue } from '@nirvana/shared/code-editor';
import { editor } from 'monaco-editor-core';

export class GzQueryInputWebComponent extends LitElement {
  static get styles() {
    styles.push(unsafeCSS(`${editorStyles}`));
    return styles;
  }

  @property({ type: String, attribute: true, reflect: true })
  queryInputPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  runQueryBtnString = '';

  @property({ type: String, attribute: true, reflect: true })
  clearBtnString = '';

  @property({ type: String, attribute: true, reflect: true })
  helpBtnString = 'Help';

  @property({ type: String, attribute: true, reflect: true })
  toolTipInfoBtn = 'Info';

  @property({ type: String, attribute: true, reflect: true })
  toolTipHelpBtn = 'Help';

  @property({ type: String, attribute: true, reflect: true })
  hintTextString = '';

  @property({ type: Array, attribute: true, reflect: true })
  searchResults = []; // results array, TBD: data type

  @property({ type: Object, attribute: true, reflect: true })
  errorMessages = {};

  @property({ type: Number, attribute: true, reflect: true })
  maxRows = 7;

  @property({ type: Number, attribute: true, reflect: true })
  minRows = 3;

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  manualResize = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  enterKeyEnabled = false;

  @property({ type: String, attribute: true, reflect: true })
  helperHref = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  isVisibleRunQueryButton = true;

  @property({ type: Boolean, attribute: true, reflect: true })
  helpBtn = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  compactMode = false;

  @property({ type: Number, attribute: true, reflect: true })
  charactersLimit = 0;

  @query('.scrollbar.vertical')
  verticalScrollBar!: HTMLElement;

  @query('#textareaInput')
  textareaInput!: HTMLElement;

  @query('.search-input-container .wrapper')
  wrapper!: HTMLElement;

  @query('#errorMessage')
  errorMessage!: GzTextWebComponent;

  @query('#errorHint')
  errorHint!: GzTextWebComponent;

  @query('.search-dropdown')
  searchDropdown!: HTMLElement;

  @query('.hint-text')
  hintText!: GzTextWebComponent;

  @query('.suggest-widget')
  suggestWidget!: HTMLElement;

  @query('.run-query-btn')
  runQueryButton!: GzButtonWebComponent;

  @query('#clear-btn')
  clearButton!: GzButtonWebComponent;

  // When set to TRUE, this property will enable the edit mode for the textarea field
  @state()
  private editable = true;

  @state()
  private dropdownOpen = false;

  @state()
  private displayHint = false;

  @state()
  private displayInlineClearButton = false;

  @state()
  private querySubmited = false;

  @state()
  private inputHasValue = false;

  @state()
  private hidePlaceHolder = false;

  @state()
  private charactersCount = 0;

  private initialListItems: Array<string> | null = null;

  private codeEditor: CodeEditor;

  private verticalScrollWidth = 5;

  private inputHeight = 20;

  private inputPaddingTop = 6; // including 1px top&bottom border

  private minInputHeight = this.inputHeight * 3 + this.inputPaddingTop;

  private maxInputHeight = this.inputHeight * 7 + this.inputPaddingTop;

  public debouncedExpandWindowByRowCount = debounce(this.expandWindowByRowCount, 300);

  constructor() {
    super();

    this.updateComplete.then(() => {
      this.dispatchEvent(
        new CustomEvent(SearchQueryPanelEvents.UPDATE_COMPLETE, {
          detail: {},
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  render() {
    return html`
      <div class="search-form">
        <div class="search-form--inner w-full d-flex flex-row">
          ${this.renderDropdown()}
          <div class="search-input-container ${this.compactMode ? '' : 'mr-2'}">
            ${this.renderTextareaInput()}
            <gz-text class="hint-text ${this.displayHint ? 'visible' : ''}" fontweight="300"
              >${this.hintTextString}
            </gz-text>

            <div class="error-container error-text ${this.isError ? 'visible' : ''}">
              <div>
                <gz-text fontSize="${FontSizes.PARAGRAPH_10}" lineHeight="1.5" id="errorMessage"></gz-text>
              </div>
              <gz-text fontSize="10" lineHeight="1.5" id="errorHint"></gz-text>
            </div>
            ${this.renderCharactersCount()}
          </div>
          <div class="search-btn-container d-inline-flex">
            <slot name="custom-button"></slot>
            ${!this.compactMode ? this.renderClearButton() : nothing}
            ${this.isVisibleRunQueryButton && !this.compactMode ? this.renderRunQueryButton() : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private renderCharactersCount() {
    if (!this.charactersLimit) {
      return nothing;
    }
    return html`
      <span class="characters-count ${this.isError ? 'characters-error' : ''}"
        >${this.charactersCount} / ${this.charactersLimit}</span
      >
    `;
  }

  private renderClearButton() {
    return html` <gz-button
      id="clear-btn"
      @click="${this.handleClearButtonClick}"
      class="mt-1 mr-2"
      type="quiet"
      color="secondary"
      buttonheight="small"
      buttonfontsize="small"
      lowercase
      >${this.clearBtnString}
    </gz-button>`;
  }

  private renderRunQueryButton() {
    return html` <gz-button class="run-query-btn" type="outline" class="ml-3" color="primary" @click="${this.runQuery}">
      ${this.runQueryBtnString}
    </gz-button>`;
  }

  private async onChangeText() {
    const input = await this.getValue();
    this.charactersCount = input.raw.length;
    if (this.charactersLimit) {
      this.isError = this.charactersCount > this.charactersLimit;
    }

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          input: input.raw,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private runQuery(): void {
    const inputValue = this.codeEditor.getText();

    this.dispatchEvent(
      new CustomEvent(SearchEvents.RUN_QUERY, {
        detail: {
          input: inputValue,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private checkIfEmpty(): void {
    this.updateComplete.then(() => {
      this.codeEditor.getValue().then(r => {
        this.inputHasValue = !isEmpty(r.raw);
      });
    });
  }

  private renderPlaceholderInput(): TemplateResult | undefined {
    this.checkIfEmpty();
    if (!isEmpty(this.queryInputPlaceholder) && this.inputHasValue) {
      return;
    }
    return html`<gz-text class="placeholder" fontSize="12">${this.queryInputPlaceholder}</gz-text>`;
  }

  private renderTextareaInput(): TemplateResult {
    let template: TemplateResult;

    template = this.editable
      ? html` <div class="wrapper">
          ${!this.compactMode ? this.renderHelpBtn() : nothing}
          ${!this.hidePlaceHolder ? this.renderPlaceholderInput() : nothing}
          <gz-icon-button
            @click="${this.handleClearButtonClick}"
            icon="ds-icon-clear"
            fontsize="20"
            buttoncolor="secondary"
            buttonfill="quiet"
            buttontype="square"
            size="24"
            class="inline-clear-button ${this.displayInlineClearButton ? 'visible' : ''}"
          ></gz-icon-button>

          <div
            id="textareaInput"
            class="editor-wrapper pl-1"
            @keyup="${this.handleTextareaKeyUp}"
            @keydown="${this.handleTextareaKeydown}"
            @change="${this.updateTextareaDisplay}"
          ></div>
        </div>`
      : html` <div class="dynamic-content-wrapper" @click="${() => this.setEditable(true)}" id="textareaString">
          ${this.setDynamicContent()}
        </div>`;

    return template;
  }

  private renderHelpBtn(): TemplateResult {
    if (this.helpBtn) {
      return html` <gz-tooltip class="info-button" tooltiptext="${this.toolTipHelpBtn}">
        <gz-button
          @click="${this.handleInfoButtonClick}"
          fontsize="10"
          color="secondary"
          type="quiet"
          buttonheight="small"
        >
          <div class="d-flex align-items-center">
            <span class="help-btn">${this.helpBtnString}</span><span class="ds-icon-expand help-btn-icon"></span>
          </div>
        </gz-button>
      </gz-tooltip>`;
    }
    return html` <gz-tooltip class="info-button" tooltiptext="${this.toolTipInfoBtn}">
      <gz-icon-button
        @click="${this.handleInfoButtonClick}"
        icon="ds-icon-help_support"
        fontsize="14"
        buttoncolor="secondary"
        buttonfill="quiet"
        buttontype="square"
        size="24"
      ></gz-icon-button>
    </gz-tooltip>`;
  }

  public setDynamicContent(): TemplateResult | undefined {
    // to add the cip elements after query submit
    return html`<span>cip</span><span> and</span>`;
  }

  public setEditable(isEditable: boolean) {
    this.editable = isEditable;
  }

  public setInitialListItems(listItems: any | null) {
    this.initialListItems = listItems;
  }

  public setSearchResultsItems(listItems: any | null) {
    this.searchResults = listItems;
  }

  public handleInfoButtonClick(): void {
    if (this.helperHref) {
      window.open(this.helperHref, '_blank');
    }

    this.dispatchEvent(
      new CustomEvent(SearchEvents.HELP_FOR_QUERY_ON_CLICK, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  public handleClearButtonClick() {
    this.codeEditor.setValue('');
    this.textareaInput.focus();
    this.dispatchEvent(
      new CustomEvent(SearchEvents.RUN_QUERY, {
        detail: {
          input: '',
        },
        bubbles: true,
        composed: true,
      })
    );
    // this will trigger the `adjustSizeOnType` method that resizes the textarea and `handleTextareaChange`
    this.textareaInput.dispatchEvent(new CustomEvent(InputEvents.CHANGE));
  }

  private handleTextareaFocus(): void {
    this.showHint();
    this.updateComplete.then(() => {
      this.alignDropDown();
    });
    this.debouncedExpandWindowByRowCount();

    if (!this.dropdownOpen) {
      this.dropdownOpen = true;
      this.codeEditor.trigger('editor', 'hideSuggestWidget', []);
    }
    elementClassListAdd(this, 'active');
  }

  private handleTextareaKeyUp(event): void {
    const value = this.codeEditor.getText();
    if (isEmpty(value)) {
      this.dropdownOpen = true;
      this.hideSuggestionWidget();
    }

    if (event.ctrlKey && event.key === 'Enter') {
      this.debouncedExpandWindowByRowCount();
    }
  }

  private handleTextareaKeydown(event: KeyboardEvent): void {
    this.debouncedExpandWindowByRowCount();
    this.setInputValidation([], false);

    if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.key === 'Enter') {
      if (!this.suggestWidget?.classList.contains('visible')) {
        this.runQuery();
        this.blur();
      } else {
        event.preventDefault();
      }
    }

    if (event.ctrlKey && event.key === '/') {
      this.hideSuggestionWidget();
    }

    this.dropdownOpen = event.ctrlKey && event.key === '/';

    if (this.dropdownOpen) {
      this.alignDropDown();
    }

    this.updateTextareaDisplay();
  }

  private hideSuggestionWidget() {
    this.codeEditor.trigger('editor', 'hideSuggestWidget', []);
  }

  private alignDropDown(): void {
    const offset = 27;
    const searchDropdownMarginTop = this.wrapper.getBoundingClientRect().height - offset;
    if (this.searchDropdown) {
      this.searchDropdown.style.marginTop = searchDropdownMarginTop + 'px';
    }
  }

  public setInputRows(minRow = 3, maxRow = 7) {
    this.minInputHeight = minRow * this.inputHeight + this.inputPaddingTop;
    this.maxInputHeight = maxRow * this.inputHeight + this.inputPaddingTop;
    this.minRows = minRow;
    this.maxRows = maxRow;
  }

  public setManualResizeMinAndMaxHeight(minRow = 3, maxRow = 30, viewPortPercentage = false) {
    const minInputHeight = minRow * this.inputHeight + this.inputPaddingTop;
    let maxInputHeight = maxRow * this.inputHeight + this.inputPaddingTop;

    this.style.setProperty('--input-manual-resize-min-height', minInputHeight + 'px');
    this.style.setProperty('--input-manual-resize-max-height', maxInputHeight + 'px');

    if (viewPortPercentage) {
      maxInputHeight = maxRow;
      this.style.setProperty('--input-manual-resize-max-height', maxInputHeight + 'vh');
    }
  }

  private expandWindowByRowCount(): void {
    // use this.debouncedExpandWindowByRowCount() instead
    const inputActive = this.classList.contains('active');
    const contentHeight = this.codeEditor.getContentHeight();
    const inputRowPadding = 2;
    const contentHeightOffset = contentHeight + inputRowPadding;
    const numberOfLines = this.codeEditor.getViewModel().getLineCount();
    this.verticalScrollBar.style.width = this.verticalScrollWidth + 'px';

    if (inputActive) {
      if (numberOfLines <= this.minRows) {
        // smaller than min rows
        this.style.setProperty('--input-dynamic-height', this.minInputHeight + 'px');
      } else if (numberOfLines <= this.maxRows) {
        //smaller than max rows
        this.style.setProperty('--input-dynamic-height', contentHeightOffset + 'px');
      } else {
        //bigger than max rows
        this.style.setProperty('--input-dynamic-height', this.maxInputHeight + 'px');
      }
    } else {
      // size for the inactive state
      this.style.setProperty('--input-dynamic-height', this.minInputHeight + 'px');
    }
  }

  private collapseWindow(): void {
    this.style.setProperty('--input-dynamic-height', this.minInputHeight + 'px');
    this.resetManualResize();
    this.verticalScrollBar.style.width = this.verticalScrollWidth + 'px';
    this.codeEditor.revealLine(1);
  }

  public setInputValidation(errors: Array<string>, onSubmit: boolean) {
    // do input validation
    let message = '';

    if (!errors.length) {
      this.errorMessage.innerText = message;
      // TODO: set value as valid

      this.isError = false;
      if (onSubmit) {
        this.querySubmited = true;
        this.collapseWindow();
        this.resetManualResize();
      }
      return;
    }
    message = this.errorMessages[errors[1]];

    this.errorHint.innerText = errors[0];
    this.errorMessage.innerText = message;

    this.displayHint = false;
    this.isError = true;
    // TODO: set value as invalid
  }

  private handleTextareaBlur() {
    setTimeout(() => {
      this.dropdownOpen = false;
      elementClassListRemove(this, 'active');
    }, 100);
  }

  private handleInputContentChange(): void {
    if (this.querySubmited) {
      setTimeout(() => {
        this.querySubmited = !this.querySubmited;
      }, 300);
      return;
    }
    this.debouncedExpandWindowByRowCount();
  }

  private handleFocusTextEditor(): void {
    this.hidePlaceHolder = true;
    this.debouncedExpandWindowByRowCount();
  }

  private handleBlurTextEditor(): void {
    this.hidePlaceHolder = false;
    this.collapseWindow();
  }

  private handleLayoutChange(): void {
    this.updateComplete.then(() => {
      this.alignDropDown();
    });
  }

  private handleInputPaste(): void {
    this.debouncedExpandWindowByRowCount();
  }

  private renderDropdown(): TemplateResult | undefined {
    if (!this.textareaInput) {
      return;
    }

    const content: string[] | any = this.initialListItems;

    if (isEmpty(content)) {
      return;
    }

    return html` <div @focus="${() => {}}" class="search-dropdown ${this.dropdownOpen ? 'visible' : ''}">
      ${this.renderGroupedListWrapper(content)}
    </div>`;
  }

  private renderGroupedListWrapper(results): TemplateResult | undefined {
    return html` <div class="wrapper">${this.renderGroupedList(results)}</div>`;
  }

  //object list
  private renderGroupedList(results: any): TemplateResult | undefined {
    //to be changed when the interface is added

    return results.map(res => {
      const sectionClass = res.prefix.replace('.', '');

      return html` <div class="container-group-list">
        ${this.renderSectionTitle(sectionClass, res.groupTitle)}
        <div class="element-list">
          ${res.value.map(
            item =>
              html` <gz-list-item @mousedown="${() => this.handleListItemSelect(item, res.prefix, res.suffix)}"
                >${item}
              </gz-list-item>`
          )}
        </div>
      </div>`;
    });
  }

  private renderSectionTitle(sectionClass, groupTitle: string): TemplateResult | undefined {
    if (!groupTitle) {
      return;
    }

    return html` <gz-title fontWeight="600" fontSize="12" class="group-title ${sectionClass}">${groupTitle}</gz-title>`;
  }

  // normal list
  private buildList(results: string[]): TemplateResult[] {
    return results.map(
      res => html` <gz-list-item @mousedown="${() => this.handleListItemSelect(res, '', '')}">${res}</gz-list-item>`
    );
  }

  private async handleListItemSelect(res: string, prefix: string, suffix: string) {
    let value = res;
    if (prefix) {
      value = prefix + value;
    }
    if (suffix) {
      value = value + suffix;
    }

    await this.addSelected(value);
    this.dropdownOpen = false;
    this.updateTextareaDisplay();
  }

  public async addSelected(element: string) {
    const value = await this.codeEditor.getValue();
    const formattedValue =
      value.raw.length === 0 ? value.raw : value.raw[value.raw.length - 1] !== ' ' ? value.raw + ' ' : value.raw;
    const newValue = formattedValue + element;
    this.codeEditor.setValue(newValue);
    this.codeEditor.setPosition({
      lineNumber: 1,
      column: newValue.length + 1,
    });
    this.codeEditor.focus();
  }

  public showHint(): void {
    this.displayHint = true;
  }

  private updateInlineClearIconPosition() {
    const { scrollHeight, offsetHeight } = this.textareaInput;
    scrollHeight > offsetHeight ? elementClassListAdd(this, 'is-wrapped') : elementClassListRemove(this, 'is-wrapped');
  }

  private updateTextareaDisplay() {
    this.updateInlineClearIconPosition();
  }

  updated(properties: PropertyValues): void {
    if (properties.has('enterKeyEnabled')) {
      //separated for live search
      if (this.codeEditor && !this.enterKeyEnabled) {
        this.codeEditor.addCommand(CodeEditor.KeyCode.Enter, function () {});
      }
    }
  }

  public setupCodeEditor(
    language: string,
    languageOptions: LuceneModelOptions | {},
    editorOptions: EditorOptions | {}
  ) {
    const options: LuceneCodeEditorOptions = {
      language: language,
      editorOptions: { ...DEFAULT_CODE_EDITOR_OPTIONS.editorOptions, ...editorOptions },
      languageOptions: { ...DEFAULT_CODE_EDITOR_OPTIONS.languageOptions, ...languageOptions },
    };

    const theme = options?.editorOptions?.theme;
    const themeConfig = theme ? THEMES[theme] : null;

    if (theme && themeConfig) {
      CodeEditor.defineTheme(theme, themeConfig);
    }

    if (this.codeEditor) {
      this.codeEditor.updateOptions(options);
      return;
    }

    this.codeEditor = CodeEditor.create(this.textareaInput, options);

    this.codeEditor.on('validityChange', this.codeEditorValidityChange.bind(this));
    this.codeEditor.onDidBlurEditorWidget(this.handleTextareaBlur.bind(this));
    this.codeEditor.onDidFocusEditorWidget(this.handleTextareaFocus.bind(this));
    this.codeEditor.onDidContentSizeChange(this.handleInputContentChange.bind(this));
    this.codeEditor.onDidLayoutChange(this.handleLayoutChange.bind(this));
    this.codeEditor.onDidFocusEditorText(this.handleFocusTextEditor.bind(this));
    this.codeEditor.onDidBlurEditorText(this.handleBlurTextEditor.bind(this));
    this.codeEditor.onDidPaste(this.handleInputPaste.bind(this));
    this.codeEditor.onDidChangeModelContent(this.onChangeText.bind(this));

    this.codeEditor.addAction({
      id: 'blockKeys',
      label: 'Disabled Action',
      keybindings: [CodeEditor.KeyCode.F1, CodeEditor.KeyCode.F3, CodeEditor.KeyCode.F8],
      run: () => {},
    });

    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.CtrlCmd | CodeEditor.KeyCode.KeyF, function () {});
    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.Alt | CodeEditor.KeyCode.F1, function () {});
    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.Alt | CodeEditor.KeyCode.F8, function () {});
    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.Shift | CodeEditor.KeyMod.Alt | CodeEditor.KeyCode.F8, function () {});
    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.CtrlCmd | CodeEditor.KeyCode.KeyG, function () {});
    //tslint:disable-next-line:no-bitwise
    this.codeEditor.addCommand(CodeEditor.KeyMod.CtrlCmd | CodeEditor.KeyCode.KeyH, function () {});
    //tslint:disable-next-line:no-bitwise
  }

  private async codeEditorValidityChange() {}

  public setValue(value: string) {
    this.codeEditor.setValue(value);
  }

  public async getValue(): Promise<CodeEditorValue> {
    return await this.codeEditor.getValue();
  }

  public resetManualResize() {
    this.wrapper.style.removeProperty('height');
  }

  public setModelMarkers(markers) {
    this.codeEditor.setModelMarkers(markers);
  }

  public getLineFirstNonWhitespaceColumn(lineNumber): number {
    return this.codeEditor.getLineFirstNonWhitespaceColumn(lineNumber);
  }

  public getLineLastNonWhitespaceColumn(lineNumber): number {
    return this.codeEditor.getLineLastNonWhitespaceColumn(lineNumber);
  }

  public setDecorations(decorations: editor.IModelDeltaDecoration[]) {
    this.codeEditor.setDecorations(decorations);
  }
}
